/**
 * A translated sentence with an ELEMENT in it — "We sent six digits to
 * **+36 30 …**".
 *
 * The whole sentence is the translation key, because word order differs
 * between languages; the bold part cannot be a separate fragment glued on.
 * So call `tr()` WITHOUT that variable — `translate` leaves an unfilled
 * `{name}` exactly as written — and hand the result here, which swaps each
 * `{name}` for its node wherever the translation put it.
 */
import { Fragment, type ReactNode } from 'react';

/**
 * The locale to hand `Intl`. English is formatted the British way — "20
 * September 2026", day first — which is what every screen here printed
 * before it had a second language; any other locale is itself.
 */
/**
 * The tag `Intl` formats with. Since step 73 the locale carries its region
 * (`he-IL`, `en-US`) and is used as it is; a bare `en` (older callers and
 * tests) keeps the day-month English this app always had.
 */
export function intlLocale(locale: string): string {
  return locale === 'en' ? 'en-GB' : locale;
}

export function rich(
  text: string,
  parts: Readonly<Record<string, ReactNode>>,
  /** Wraps the sentence's own words (not the parts) — e.g. to fade them on their own. */
  words?: (word: string) => ReactNode,
): ReactNode {
  const pieces = text.split(/(\{\w+\})/g);
  return pieces.map((piece, i) => {
    const name = /^\{(\w+)\}$/.exec(piece)?.[1];
    if (name && name in parts) return <Fragment key={i}>{parts[name]}</Fragment>;
    return <Fragment key={i}>{words && piece.trim() ? words(piece) : piece}</Fragment>;
  });
}
